import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  Select,
  Button,
  Group,
  Alert,
  Box,
  Badge,
  Switch,
  TextInput,
  Progress,
  Table
} from '@mantine/core';
import {
  IconDownload,
  IconCheck,
  IconX,
  IconInfoCircle,
  IconCalendar,
  IconFileTypePdf,
  IconFileTypeCsv,
  IconFileTypeXls,
  IconCode
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

export type ExportFormat = 'json' | 'csv' | 'xlsx' | 'pdf';

interface ExportScope {
  dateRange: {
    start: Date;
    end: Date;
  };
  buildings: string[];
  floors: string[];
  rooms: string[];
  sessionTimes: string[];
  includeAssignments: boolean;
  includeBlocks: boolean;
  includeConflicts: boolean;
  includeUtilization: boolean;
  includeMetadata: boolean;
}

interface ExportProgress {
  step: string;
  progress: number;
  total: number;
  current: number;
}

interface ExportModalProps {
  opened: boolean;
  onClose: () => void;
  onExport: (config: {
    format: ExportFormat;
    scope: ExportScope;
    filename?: string;
  }) => Promise<{
    success: boolean;
    downloadUrl?: string;
    filename?: string;
    error?: string;
  }>;
  defaultScope?: Partial<ExportScope>;
  loading?: boolean;
}

/**
 * M4: Export Modal - Export planner data in multiple formats
 * 
 * Features:
 * - Multiple export formats (JSON, CSV, XLSX, PDF)
 * - Configurable export scope and filters
 * - Progress tracking during export
 * - Download management
 * - Custom filename support
 * - Export preview and validation
 */
const ExportModal: React.FC<ExportModalProps> = ({
  opened,
  onClose,
  onExport,
  defaultScope,
  loading = false
}) => {
  const [format, setFormat] = useState<ExportFormat>('json');
  const [filename, setFilename] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [scope, setScope] = useState<ExportScope>({
    dateRange: {
      start: new Date(),
      end: new Date()
    },
    buildings: [],
    floors: [],
    rooms: [],
    sessionTimes: [],
    includeAssignments: true,
    includeBlocks: true,
    includeConflicts: false,
    includeUtilization: false,
    includeMetadata: true,
    ...defaultScope
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (opened) {
      setFormat('json');
      setFilename('');
      setExporting(false);
      setProgress(null);
      
      // Generate default filename
      const today = new Date().toISOString().split('T')[0];
      setFilename(`planner-export-${today}`);
    }
  }, [opened]);

  // Update filename when format changes
  useEffect(() => {
    if (filename && !exporting) {
      const baseName = filename.replace(/\.(json|csv|xlsx|pdf)$/, '');
      setFilename(`${baseName}.${format}`);
    }
  }, [format]);

  const handleExport = async () => {
    if (!filename.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please enter a filename',
        color: 'red'
      });
      return;
    }

    setExporting(true);
    setProgress({
      step: 'Preparing export...',
      progress: 0,
      total: 100,
      current: 0
    });

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (!prev) return null;
          const newProgress = Math.min(prev.progress + 10, 90);
          return {
            ...prev,
            progress: newProgress,
            step: newProgress < 30 ? 'Collecting data...' :
                   newProgress < 60 ? 'Processing assignments...' :
                   newProgress < 90 ? 'Generating file...' :
                   'Finalizing export...'
          };
        });
      }, 200);

      const result = await onExport({
        format,
        scope,
        filename: filename.trim()
      });

      clearInterval(progressInterval);

      if (result.success) {
        setProgress({
          step: 'Export completed!',
          progress: 100,
          total: 100,
          current: 100
        });

        notifications.show({
          title: 'Export Successful',
          message: `File "${result.filename}" has been downloaded`,
          color: 'green',
          icon: <IconCheck size={16} />
        });

        // Auto-close after success
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      notifications.show({
        title: 'Export Failed',
        message: error instanceof Error ? error.message : 'Failed to export data',
        color: 'red',
        icon: <IconX size={16} />
      });
      setProgress(null);
    } finally {
      setExporting(false);
    }
  };

  const handleClose = () => {
    if (!exporting) {
      onClose();
      setProgress(null);
    }
  };

  const formatOptions = [
    {
      value: 'json' as const,
      label: 'JSON',
      description: 'Structured data format',
      icon: <IconCode size={16} />
    },
    {
      value: 'csv' as const,
      label: 'CSV',
      description: 'Spreadsheet format',
      icon: <IconFileTypeCsv size={16} />
    },
    {
      value: 'xlsx' as const,
      label: 'Excel',
      description: 'Microsoft Excel format',
      icon: <IconFileTypeXls size={16} />
    },
    {
      value: 'pdf' as const,
      label: 'PDF',
      description: 'Printable report format',
      icon: <IconFileTypePdf size={16} />
    }
  ];

  const canExport = filename.trim() && !exporting && !loading;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="sm">
          <IconDownload size={20} />
          <Text fw={600}>Export Planner Data</Text>
        </Group>
      }
      size={400}
      centered
      closeOnClickOutside={false}
      closeOnEscape={!exporting}
      trapFocus
      returnFocus
    >
      <Stack gap="md">
        {!progress ? (
          <>
            {/* Format Selection */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Export Format *</Text>
              <Select
                placeholder="Choose export format..."
                data={formatOptions.map(option => ({
                  value: option.value,
                  label: option.label,
                  description: option.description
                }))}
                value={format}
                onChange={(value) => value && setFormat(value as ExportFormat)}
                disabled={loading || exporting}
                required
              />
            </Box>

            {/* Filename */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Filename *</Text>
              <TextInput
                placeholder="Enter filename..."
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                disabled={loading || exporting}
                required
              />
            </Box>

            {/* Export Scope */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Export Scope</Text>
              <Stack gap="sm">
                <Group gap="sm">
                  <IconCalendar size={16} color="var(--mantine-color-blue-6)" />
                  <Text size="sm">
                    <strong>Date Range:</strong> {scope.dateRange.start.toLocaleDateString()} - {scope.dateRange.end.toLocaleDateString()}
                  </Text>
                </Group>

                <Switch
                  label="Include Assignments"
                  description="Export all room assignments and schedules"
                  checked={scope.includeAssignments}
                  onChange={(e) => setScope(prev => ({
                    ...prev,
                    includeAssignments: e.currentTarget.checked
                  }))}
                  disabled={loading || exporting}
                />

                <Switch
                  label="Include Blocks"
                  description="Export room blocks and maintenance schedules"
                  checked={scope.includeBlocks}
                  onChange={(e) => setScope(prev => ({
                    ...prev,
                    includeBlocks: e.currentTarget.checked
                  }))}
                  disabled={loading || exporting}
                />

                <Switch
                  label="Include Conflicts"
                  description="Export conflict analysis and resolution data"
                  checked={scope.includeConflicts}
                  onChange={(e) => setScope(prev => ({
                    ...prev,
                    includeConflicts: e.currentTarget.checked
                  }))}
                  disabled={loading || exporting}
                />

                <Switch
                  label="Include Utilization"
                  description="Export room utilization statistics"
                  checked={scope.includeUtilization}
                  onChange={(e) => setScope(prev => ({
                    ...prev,
                    includeUtilization: e.currentTarget.checked
                  }))}
                  disabled={loading || exporting}
                />

                <Switch
                  label="Include Metadata"
                  description="Export system metadata and timestamps"
                  checked={scope.includeMetadata}
                  onChange={(e) => setScope(prev => ({
                    ...prev,
                    includeMetadata: e.currentTarget.checked
                  }))}
                  disabled={loading || exporting}
                />
              </Stack>
            </Box>

            {/* Format Information */}
            <Alert
              icon={<IconInfoCircle size={16} />}
              title={`${format.toUpperCase()} Export`}
              color="blue"
              variant="light"
            >
              <Text size="sm">
                {format === 'json' && 'Structured data format suitable for data analysis and integration.'}
                {format === 'csv' && 'Comma-separated values format compatible with spreadsheet applications.'}
                {format === 'xlsx' && 'Microsoft Excel format with multiple sheets and formatting.'}
                {format === 'pdf' && 'Portable document format suitable for printing and sharing.'}
              </Text>
            </Alert>

            {/* Loading State */}
            {loading && (
              <Box ta="center" py="md">
                <Text size="sm" c="dimmed">
                  Preparing export options...
                </Text>
              </Box>
            )}

            {/* Action Buttons */}
            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                onClick={handleClose}
                disabled={exporting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleExport}
                disabled={!canExport}
                loading={exporting}
                leftSection={<IconDownload size={16} />}
              >
                {exporting ? 'Exporting...' : 'Export Data'}
              </Button>
            </Group>
          </>
        ) : (
          <>
            {/* Export Progress */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Export Progress</Text>
              <Progress
                value={progress.progress}
                color="blue"
                size="lg"
                animated
                striped
              />
              <Text size="sm" c="dimmed" mt="xs" ta="center">
                {progress.step} ({progress.current}/{progress.total})
              </Text>
            </Box>

            {/* Progress Details */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Export Details</Text>
              <Table striped>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td><strong>Format:</strong></Table.Td>
                    <Table.Td>{format.toUpperCase()}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><strong>Filename:</strong></Table.Td>
                    <Table.Td>{filename}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><strong>Scope:</strong></Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {scope.includeAssignments && <Badge size="xs" color="blue">Assignments</Badge>}
                        {scope.includeBlocks && <Badge size="xs" color="orange">Blocks</Badge>}
                        {scope.includeConflicts && <Badge size="xs" color="red">Conflicts</Badge>}
                        {scope.includeUtilization && <Badge size="xs" color="green">Utilization</Badge>}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Box>

            {progress.progress === 100 && (
              <Alert
                icon={<IconCheck size={16} />}
                title="Export Completed"
                color="green"
                variant="light"
              >
                <Text size="sm">
                  Your export has been generated and downloaded successfully.
                </Text>
              </Alert>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default ExportModal;
